import React, { useState, useContext } from "react";
import { EARLY_PAY, saving } from "helpers/earlyPay";
import { PageWrapper, PageContent } from "./styled";
import { Outlet, useNavigate } from "react-router-dom";
import NavbarLayout from "./NavbarLayout";
import usePageTracking  from "../hooks/usePageTracking";
import useDiscountEligibility from "../hooks/useDiscountEligibility";
import DiscountInfoModal from "containers/Popups/DiscountInfo";
import PolicyGate from "containers/PolicyGate";
import { themevals } from "theme/themevals";
import { hideDiscountPopup, applyDiscount } from "api/Quotes";
import { getInvoicePDF, emailInvoice, getInvoice } from "api/Invoices";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import useToast from "components/toast/useToast";
import LoginAnnouncement from "components/Announcement/LoginAnnouncement";
import { claimLoginModal } from "helpers/loginModal";

const BaseLayout = () =>{
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);
  const { add: addToast } = useToast();
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  // One pop-up per login (helpers/loginModal): the Discount Reminder waits
  // until the announcement has decided whether it is showing.
  const [announcementDecision, setAnnouncementDecision] = useState<null | boolean>(null);
  const { projects, hasEligibleProjects, isLoading, refetch } = useDiscountEligibility();
  const [localProjects, setLocalProjects] = useState(projects);

  const [submittingSendQuoteIds, setSubmittingSendQuoteIds] = useState<Set<string>>(new Set());
  const [invoiceIdMap, setInvoiceIdMap] = useState<Map<string, string>>(new Map());
  const [billingEmailMap, setBillingEmailMap] = useState<Map<string, string>>(new Map());

  if (process.env.REACT_APP_ENVIRONMENT === "production") {
    usePageTracking();
  }

  // Sync local projects with hook projects
  React.useEffect(() => {
    setLocalProjects(projects);
  }, [projects]);

  // Format projects for the modal
  const formattedProjects = localProjects.map((project) => {
    const currentTotal = project.quoteTotal;
    // Savings on the goods only (backend discountBase excludes the fuel
    // surcharge); the amounts to pay keep it.
    const base = Number(project.discountBase ?? currentTotal);
    const discount5Amount = saving(base, EARLY_PAY.fullPct);
    const discount3Amount = saving(base, EARLY_PAY.depositPct);
    const discount5Total = currentTotal - discount5Amount;
    const discount3Total = currentTotal - discount3Amount;
    
    return {
      projectId: project.projectId,
      projectNumber: project.projectNumber,
      quoteId: project.quoteId,
      currentTotal: `${currentTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      discount5Total: `${discount5Total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      discount3Total: `${discount3Total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    };
  });

  // Load invoice IDs and billing emails from projects data
  React.useEffect(() => {
    const newInvoiceIdMap = new Map<string, string>();
    const newBillingEmailMap = new Map<string, string>();
    
    for (const project of localProjects) {
      if (project.invoiceId) {
        newInvoiceIdMap.set(project.quoteId, project.invoiceId);
      }
      if (project.billingEmail) {
        newBillingEmailMap.set(project.quoteId, project.billingEmail);
      }
    }
    
    setInvoiceIdMap(newInvoiceIdMap);
    setBillingEmailMap(newBillingEmailMap);
  }, [localProjects]);

  const handleDontRemindMe = async (quoteId: string) => {
    console.log(`Hiding discount popup for quote: ${quoteId}`);
    try {
      await hideDiscountPopup(quoteId);
      
      // Find the project to get the projectId for tracking
      const project = localProjects.find(p => p.quoteId === quoteId);
      
      // Track the activity
      if (project) {
        trackActivity({
          module: "discount",
          action: "discount_no_reminder",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          metadata: {
            quoteId: quoteId,
            projectId: project.projectId
          }
        });
      }
      
      // Immediately filter out the project from the local list, only close if none remain
      setLocalProjects(prev => {
        const updated = prev.filter(project => project.quoteId !== quoteId);
        if (updated.length === 0) {
          setShowDiscountModal(false);
        }
        return updated;
      });
      
      // Refetch to ensure we have the latest data from the backend
      await refetch();
      
    } catch (error: any) {
      console.error("Error hiding discount popup:", error);
    }
  };

  const handleViewOrPayInvoice = (projectNumber: string, quoteId: string, projectId: string) => {
    const invoiceId = invoiceIdMap.get(quoteId);
    if (!invoiceId) {
      addToast("Invoice ID not found", themevals.colors.danger);
      return;
    }

    // Track view activity is handled in the modal component.
    // Open the invoice on its pay chooser (clients only; staff ignore ?pay).
    // Same tab (2026-10-08): paying is the next step, not a side trip.
    setShowDiscountModal(false);
    navigate(`/invoices/${invoiceId}?pay=1`);
  };

  const handleSendInvoice = async (projectNumber: string, quoteId: string) => {
    const invoiceId = invoiceIdMap.get(quoteId);
    if (!invoiceId) {
      addToast("Invoice ID not found", themevals.colors.danger);
      return;
    }

    // Add to submitting set
    setSubmittingSendQuoteIds(prev => new Set(prev).add(quoteId));

    try {
      const res = await emailInvoice({ data: { id: invoiceId } });
      
      if (res.success) {
        const billingEmail = billingEmailMap.get(quoteId) || "billing";
        addToast(`Invoice sent to ${billingEmail}`, themevals.colors.success);
        
        // Track the activity
        const project = localProjects.find(p => p.quoteId === quoteId);
        if (project) {
          trackActivity({
            module: "discount",
            action: "send_discount_invoice",
            actionType: "update",
            userId: user?.id || null,
            companyId: user?.company?.id || null,
            metadata: {
              quoteId: quoteId,
              projectId: project.projectId,
              invoiceId: invoiceId
            }
          });
        }
      } else {
        addToast("Failed to send invoice", themevals.colors.danger);
      }
    } catch (error: any) {
      console.error("Error sending invoice:", error);
      addToast("Error sending invoice", themevals.colors.danger);
    } finally {
      // Remove from submitting set
      setSubmittingSendQuoteIds(prev => {
        const next = new Set(prev);
        next.delete(quoteId);
        return next;
      });
    }
  };

  const handleClose = () => {
    console.log("Closing modal...");
    setShowDiscountModal(false);
  };

  // Show modal when eligible projects are found and not loading
  React.useEffect(() => {
    if (!isLoading && hasEligibleProjects && announcementDecision === false && claimLoginModal("discount")) {
      setShowDiscountModal(true);
    }
  }, [isLoading, hasEligibleProjects, announcementDecision]);

  // Close modal when all projects are filtered out
  // (handled directly when we remove the final project)
 
  return (
    <PolicyGate user={user}>
      <PageWrapper>
        <NavbarLayout/>
        <PageContent>
          <Outlet/>
        </PageContent>

        {/* "What's new" announcement at login (one pop-up per login). */}
        {user?.id && <LoginAnnouncement key={user.id} onDecided={(showing) => setAnnouncementDecision(showing)} />}
        
        {/* Discount Info Modal - only render if there are eligible projects */}
        {hasEligibleProjects && (
          <DiscountInfoModal
            isOpen={showDiscountModal}
            onClose={handleClose}
            onViewOrPayInvoice={handleViewOrPayInvoice}
            onSendInvoice={handleSendInvoice}
            onDontRemindMe={handleDontRemindMe}
            projects={formattedProjects}

            submittingSendQuoteIds={submittingSendQuoteIds}
            billingEmails={billingEmailMap}
            discountPercentage8={EARLY_PAY.fullPct}
            discountPercentage3={3}
            depositPercentage100={100}
            depositPercentage50={50}
            paymentDeadlineDays={5}
          />
        )}
      </PageWrapper>
    </PolicyGate>
  )
}
export default BaseLayout;
